import React, { useContext, useEffect } from 'react';
import { injectIntl } from 'react-intl';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { merge, range } from 'lodash';
import { Form, FormField, Table } from 'semantic-ui-react';
import ComponentCarrier from './ComponentCarrier';
import messages from 'src/messages';
import StoredAccordion from 'src/components/StoredAccordion';
import { PMPPanelProps } from './utils';
import { useSelector } from 'react-redux';
import { FormResetContext } from '../utils';

type NDComponentCarriersPanelProps = {
  intl: any;
  isTemplate: boolean;
} & PMPPanelProps;

function NDComponentCarriersPanel(props: NDComponentCarriersPanelProps) {
  const { panel, ap, intl, isTemplate, choices, setModified } = props;
  const { formatMessage } = intl;
  const { title, name } = panel;
  const permissionWrite = useSelector(
    (state: any) => state.mainFrame.permissionWrite
  );
  const { getValues, setValue, control } = useFormContext();
  const componentCarrierQty = useWatch({
    name: 'radio_qty',
  });

  const carrierQty = useWatch({ name: 'radios.0.equipment.carrier_qty' });
  const band = useWatch({ name: 'radios.0.equipment.band' });
  const bandCarrier2 = useWatch({ name: 'radios.0.equipment.band_carrier_2' });
  const componentCarriers = range(componentCarrierQty);
  const resetValue = useContext(FormResetContext);

  // Helper function to check if a radio needs to be initialized
  const needsInitialization = (radioIndex: number): boolean => {
    const equipment = getValues(`radios.${radioIndex}.equipment`);
    const band = getValues(`radios.${radioIndex}.equipment.band`);
    return !equipment || band === '60 GHz' || band == null;
  };

  // Helper function to clone radio0 to a specific radio index
  // Only clones equipment and antennas, preserves existing power and frequency
  const cloneRadio0ToIndex = (targetIndex: number) => {
    const radio0 = getValues('radios.0');
    const existingRadio = getValues(`radios.${targetIndex}`);

    // Ensure antennas is always an array
    const antennas = Array.isArray(radio0.antennas)
      ? radio0.antennas.map((antenna) => merge({}, antenna))
      : Object.values(radio0.antennas || {}).map((antenna) =>
          merge({}, antenna)
        );

    // Preserve existing power and frequency if they exist
    const newRadio = {
      equipment: merge({}, radio0.equipment),
      antennas: antennas,
      power: existingRadio?.power
        ? merge({}, existingRadio.power)
        : merge({}, radio0.power),
      frequency: existingRadio?.frequency
        ? merge({}, existingRadio.frequency)
        : {},
    };

    setValue(`radios.${targetIndex}`, newRadio);
    // Only reset tx_frequency if there wasn't an existing frequency object
    if (!existingRadio?.frequency) {
      setValue(`radios.${targetIndex}.frequency.tx_frequency`, null);
    }
  };

  // Handle special case: 60 GHz has carrierQty=1 but componentCarrierQty=2 (2 sectors)
  useEffect(() => {
    const radio0 = getValues('radios.0');
    const radio1 = getValues('radios.1');
    if (carrierQty === 1 && componentCarrierQty === 2 && !radio1) {
      // Ensure antennas is always an array
      const antennas = Array.isArray(radio0.antennas)
        ? radio0.antennas
        : Object.values(radio0.antennas || {});

      setValue('radios.1', {
        ...radio0,
        antennas: antennas,
      });
    } else if (carrierQty === 2 && bandCarrier2) {
      setValue('radios.1.equipment.band', bandCarrier2);
    }
  }, [bandCarrier2, carrierQty, componentCarrierQty, getValues, setValue]);

  // Main effect to handle component carrier quantity changes
  useEffect(() => {
    // Only process if we need multiple carriers
    if (carrierQty > 1 || componentCarrierQty > 1) {
      // Ensure we have the required number of component carriers
      for (let i = 1; i < componentCarrierQty; i++) {
        if (needsInitialization(i)) {
          cloneRadio0ToIndex(i);
        }
      }
    }

    // Always ensure radios array matches componentCarrierQty
    const currentRadios = getValues('radios');
    if (currentRadios && currentRadios.length !== componentCarrierQty) {
      setValue('radios', currentRadios.slice(0, componentCarrierQty), {
        shouldDirty: true,
      });
    }
  }, [carrierQty, componentCarrierQty, getValues, setValue]);

  const componentCarrierOptions =
    carrierQty === 2
      ? [
          { key: 2, text: 2, value: 2 },
          { key: 3, text: 3, value: 3 },
        ]
      : [
          { key: 1, text: 1, value: 1 },
          { key: 2, text: 2, value: 2 },
        ];

  return (
    <StoredAccordion
      name={name}
      title={title}
      contentProps={{ loading: ap.dirty }}
    >
      <FormField inline>
        <label>Component Carriers</label>

        <Controller
          control={control}
          name={`radio_qty`}
          render={({ field: { ref, onChange, ...rest } }) => (
            <>
              <Form.Field width={2}>
                <Form.Select
                  {...rest}
                  defaultValue={componentCarrierQty}
                  onChange={(e, data) => {
                    onChange(data.value);
                    setModified(true);
                  }}
                  options={componentCarrierOptions}
                  disabled={!permissionWrite}
                ></Form.Select>
              </Form.Field>
            </>
          )}
        ></Controller>
        <Table>
          <Table.Header style={{ fontSize: '0.818em' }}>
            <Table.Row>
              <Table.HeaderCell>
                Component <br></br> Carrier
              </Table.HeaderCell>
              <Table.HeaderCell>
                {formatMessage(messages.carrier)}
              </Table.HeaderCell>
              <Table.HeaderCell>
                {formatMessage(messages.band)}
              </Table.HeaderCell>
              <Table.HeaderCell>
                {formatMessage(messages.bandwidth)}
              </Table.HeaderCell>
              {!isTemplate && (
                <Table.HeaderCell>
                  {formatMessage(messages.frequency)}
                  <br></br>(MHz)
                </Table.HeaderCell>
              )}
              <Table.HeaderCell>
                ND Interference <br></br>(dBm in b/w)
              </Table.HeaderCell>
              <Table.HeaderCell>
                Default SM Interference <br></br>(dBm in b/w)
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {componentCarriers.map((cc, index) => {
              return (
                // resetvalue added as part of key to remount the component
                // frequencies render properly on reset form
                <ComponentCarrier
                  key={`cc-${index}-${resetValue}`}
                  {...props}
                  // As we support more carriers (beyond 2), derving the
                  // carrier index logic may become complex
                  index={index}
                  carrierIndex={carrierQty === 2 ? index : 0}
                  isTemplate={isTemplate}
                  carrierQty={carrierQty}
                ></ComponentCarrier>
              );
            })}
          </Table.Body>
        </Table>
      </FormField>
    </StoredAccordion>
  );
}

export default injectIntl(NDComponentCarriersPanel);
